// Stats Table widget (Leagues canvas) — the "data handler" showcase (FE4).
// A configurable, league-wide stats table over POST /player-stats: pick metrics
// (+per90), group-by dimensions, and position/team filters in a Configure
// drawer; the table sorts + paginates client-side. Consumes [league, season]
// (the scope supplies the competition + season the query is scoped to).
//
// The drawer settings live in the canvas store, keyed by this widget's
// instanceId — session-only (survive tab switches, reset on reload). See
// statsQuery.ts for the pure body-builder + column/sort helpers.

import { useEffect, useMemo, useState } from "react";
import { useCanvasStore } from "../store/canvas.js";
import { useMetricCategories, usePositions, useStatsQuery } from "../data/stats.js";
import { useMatches } from "../data/leagues.js";
import { teamsFromMatches } from "./matchFilter.js";
import {
  orderedColumns,
  nextSort,
  sortRows,
  activeFilterCount,
  remapKeysForCatalogue,
  DEFAULT_STATS_CONFIG,
  GROUP_BY_OPTIONS,
  type StatsConfig,
  type SortState,
  type ColumnSpec,
} from "./statsQuery.js";
import type { MetricCategory } from "../data/stats.js";
import type { WidgetScope } from "./scope.js";
import { theme } from "../lib/theme.js";

const PAGE_SIZE = 12;

/** Flatten the catalogue to a metric-key → short display name map (table heads). */
function metricLabelMap(categories: MetricCategory[] | undefined): Record<string, string> {
  const map: Record<string, string> = {};
  for (const cat of categories ?? [])
    for (const sub of cat.subcategories)
      for (const m of sub.playerMetrics) map[m.key] = m.shortName || m.name;
  return map;
}

/** The set of metric keys the catalogue currently offers (for reconciling a
 *  per90 toggle). */
function validKeySet(categories: MetricCategory[] | undefined): Set<string> {
  const set = new Set<string>();
  for (const cat of categories ?? [])
    for (const sub of cat.subcategories) for (const m of sub.playerMetrics) set.add(m.key);
  return set;
}

export default function StatsTableWidget({
  scope,
  instanceId,
}: {
  scope: WidgetScope;
  instanceId: string;
}) {
  const { competitionId, seasonId } = scope;
  const config = useCanvasStore((s) => s.configs[instanceId]) ?? DEFAULT_STATS_CONFIG;
  const setConfig = useCanvasStore((s) => s.setConfig);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sort, setSort] = useState<SortState | null>(null);
  const [page, setPage] = useState(0);

  // The applied query + the metric catalogue for its per90 mode (column labels).
  const stats = useStatsQuery(competitionId, seasonId, config);
  const metrics = useMetricCategories(config.per90);
  const labels = useMemo(() => metricLabelMap(metrics.data), [metrics.data]);

  const rows = stats.data?.rows ?? [];
  const columns: ColumnSpec[] = useMemo(
    () => (rows[0] ? orderedColumns(rows[0], config.keys, config.groupBy, labels) : []),
    [rows, config.keys, config.groupBy, labels]
  );
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);

  // Reset to the first page whenever the result set or its ordering changes.
  useEffect(() => setPage(0), [stats.data, sort]);

  if (competitionId === null || seasonId === null)
    return <Msg>Pick a league and season above.</Msg>;

  if (drawerOpen) {
    return (
      <ConfigureDrawer
        config={config}
        competitionId={competitionId}
        seasonId={seasonId}
        onApply={(next) => {
          setConfig(instanceId, next);
          setSort(null);
          setDrawerOpen(false);
        }}
        onCancel={() => setDrawerOpen(false)}
      />
    );
  }

  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sorted.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const filterCount = activeFilterCount(config);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => setDrawerOpen(true)} style={btn(true)}>
          Configure{filterCount > 0 ? ` (${filterCount})` : ""}
        </button>
        <span style={{ color: theme.inkFaint, fontSize: 12 }}>
          {stats.isSuccess ? `${sorted.length} rows` : ""}
        </span>
      </div>

      {config.keys.length === 0 ? (
        <Msg>Select at least one metric in Configure.</Msg>
      ) : stats.isPending ? (
        <Msg>Loading stats… (a full league-season can take a few seconds)</Msg>
      ) : stats.isError ? (
        <Msg danger>Couldn't load stats. {String(stats.error)}</Msg>
      ) : rows.length === 0 ? (
        <Msg>No rows for this query.</Msg>
      ) : (
        <>
          <div style={{ flex: 1, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {columns.map((c) => {
                    const active = sort?.key === c.key;
                    return (
                      <th
                        key={c.key}
                        onClick={() => setSort((s) => nextSort(s, c.key))}
                        style={{
                          ...headCell(c.numeric),
                          cursor: "pointer",
                          color: active ? theme.accent : theme.inkMuted,
                        }}
                        title="Click to sort"
                      >
                        {c.label}
                        {active ? (sort?.dir === "asc" ? " ▲" : " ▼") : ""}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${theme.border}` }}>
                    {columns.map((c) => (
                      <td key={c.key} style={bodyCell(c.numeric)}>
                        {fmt(row[c.key])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 8,
                flexShrink: 0,
              }}
            >
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                style={btn(safePage !== 0)}
              >
                Prev
              </button>
              <span style={{ color: theme.inkMuted, fontSize: 12 }}>
                {safePage + 1} / {pageCount}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                disabled={safePage >= pageCount - 1}
                style={btn(safePage < pageCount - 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── The Configure drawer ─────────────────────────────────────────────────────
// Edits a LOCAL draft of the config; Apply commits it to the store (which
// triggers the — potentially slow — query). Draft, not live, so a user can set
// several things before paying for one refetch.

function ConfigureDrawer({
  config,
  competitionId,
  seasonId,
  onApply,
  onCancel,
}: {
  config: StatsConfig;
  competitionId: number;
  seasonId: number;
  onApply: (next: StatsConfig) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<StatsConfig>(config);

  // The metric catalogue for the DRAFT's per90 mode. When per90 flips, the
  // catalogue changes (base ↔ _90 keys) — reconcile the selection to it so a
  // toggle never leaves stale keys checked.
  const metrics = useMetricCategories(draft.per90);
  useEffect(() => {
    if (!metrics.data) return;
    const valid = validKeySet(metrics.data);
    const reconciled = remapKeysForCatalogue(draft.keys, valid);
    const changed =
      reconciled.length !== draft.keys.length ||
      reconciled.some((k, i) => k !== draft.keys[i]);
    if (changed) setDraft((d) => ({ ...d, keys: reconciled }));
    // Reconcile only when the catalogue (per90) changes, not on every keystroke —
    // draft.keys is intentionally not a dependency (would loop on every toggle).
  }, [metrics.data]);

  const positions = usePositions();
  // Team list for THIS league+season — each match carries both team ids+names.
  const matchesQ = useMatches(competitionId, seasonId);
  const teams = useMemo(
    () => teamsFromMatches(matchesQ.data?.matches ?? []),
    [matchesQ.data]
  );

  const selected = new Set(draft.keys);
  const toggleKey = (key: string) =>
    setDraft((d) => ({
      ...d,
      keys: d.keys.includes(key) ? d.keys.filter((k) => k !== key) : [...d.keys, key],
    }));
  const toggleGroup = (key: string) =>
    setDraft((d) => ({
      ...d,
      groupBy: d.groupBy.includes(key)
        ? d.groupBy.filter((g) => g !== key)
        : [...d.groupBy, key],
    }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ color: theme.ink, fontSize: 14, fontWeight: 600 }}>Configure stats</span>
        <label
          style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: theme.inkMuted }}
        >
          <input
            type="checkbox"
            checked={draft.per90}
            onChange={(e) => setDraft((d) => ({ ...d, per90: e.target.checked }))}
          />
          Per 90
        </label>
      </div>

      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Metrics */}
        <Section title={`Metrics (${draft.keys.length})`}>
          {metrics.isPending ? (
            <Msg>Loading metrics…</Msg>
          ) : metrics.isError ? (
            <Msg danger>Couldn't load metrics.</Msg>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {metrics.data!.map((cat) => (
                <div key={cat.id}>
                  <div style={{ color: theme.ink, fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                    {cat.name}
                  </div>
                  {cat.subcategories.map((sub) => (
                    <div key={sub.id} style={{ marginBottom: 6 }}>
                      <div style={{ color: theme.inkFaint, fontSize: 11, marginBottom: 3 }}>
                        {sub.name}
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {sub.playerMetrics.map((m) => (
                          <Chip
                            key={m.key}
                            label={m.shortName || m.name}
                            on={selected.has(m.key)}
                            onClick={() => toggleKey(m.key)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Group by */}
        <Section title="Group by">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {GROUP_BY_OPTIONS.map((g) => (
              <Chip
                key={g.key}
                label={g.label}
                on={draft.groupBy.includes(g.key)}
                onClick={() => toggleGroup(g.key)}
              />
            ))}
          </div>
        </Section>

        {/* Filters: position + team */}
        <Section title="Filters">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ color: theme.inkFaint, fontSize: 11 }}>Position</span>
              <select
                value={draft.positionId ?? ""}
                onChange={(e) => setDraft((d) => ({ ...d, positionId: e.target.value || null }))}
                style={select}
              >
                <option value="">All positions</option>
                {(positions.data?.positions ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ color: theme.inkFaint, fontSize: 11 }}>Team</span>
              <select
                value={draft.teamId ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, teamId: e.target.value ? Number(e.target.value) : null }))
                }
                style={select}
              >
                <option value="">All teams</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </Section>
      </div>

      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        <button onClick={() => onApply(draft)} disabled={draft.keys.length === 0} style={btn(draft.keys.length > 0)}>
          Apply
        </button>
        <button onClick={onCancel} style={btn(false)}>
          Cancel
        </button>
        {draft.keys.length === 0 && (
          <span style={{ color: theme.danger, fontSize: 12, alignSelf: "center" }}>
            Select at least one metric.
          </span>
        )}
      </div>
    </div>
  );
}

// ── small presentational helpers ─────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ color: theme.ink, fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function Chip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        border: `1px solid ${on ? theme.accent : theme.border}`,
        background: on ? theme.accentSurface : "transparent",
        color: on ? theme.ink : theme.inkMuted,
        fontSize: 12,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}

function fmt(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(2);
  return v;
}

const num = {
  fontVariantNumeric: "tabular-nums" as const,
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

function headCell(numeric: boolean): React.CSSProperties {
  return {
    textAlign: numeric ? "right" : "left",
    padding: "5px 6px",
    fontWeight: 500,
    position: "sticky",
    top: 0,
    background: theme.tile,
    whiteSpace: "nowrap",
  };
}
function bodyCell(numeric: boolean): React.CSSProperties {
  return {
    textAlign: numeric ? "right" : "left",
    padding: "5px 6px",
    color: theme.ink,
    whiteSpace: "nowrap",
    ...(numeric ? num : {}),
  };
}

function btn(active: boolean): React.CSSProperties {
  return {
    padding: "5px 12px",
    borderRadius: 8,
    border: "none",
    background: active ? theme.accent : theme.surface,
    color: active ? theme.page : theme.inkMuted,
    fontSize: 13,
    fontWeight: 600,
    cursor: active ? "pointer" : "default",
  };
}

const select: React.CSSProperties = {
  background: theme.input,
  color: theme.ink,
  border: `1px solid ${theme.borderInput}`,
  borderRadius: 8,
  padding: "6px 8px",
  fontSize: 13,
  minWidth: 150,
};
